import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;

const COUNTRIES = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Netherlands",
  "Switzerland",
  "India",
  "United Arab Emirates",
  "Brazil",
  "Mexico",
  "South Africa",
  "Singapore",
  "Japan",
];

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  country: "",
  address: "",
  courseId: "",
  batchId: "",
  roomType: "",
};

const EMPTY_SUBMITTED_SUMMARY = {
  courseTitle: "Not selected",
  batchLabel: "Not selected",
  roomLabel: "Not selected",
  price: "-",
  seatsLeft: 0,
};

const cardStyle = {
  width: "100%",
  maxWidth: 980,
  margin: "40px auto",
  background: "#fff",
  borderRadius: 24,
  overflow: "hidden",
  boxShadow: "0 30px 80px rgba(17, 24, 39, 0.18)",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
};

const leftStyle = {
  background:
    "linear-gradient(135deg, rgba(127,0,0,0.95) 0%, rgba(155,0,0,0.92) 45%, rgba(192,57,43,0.9) 100%)",
  color: "#fff",
  padding: "44px 38px",
};

const rightStyle = {
  padding: "38px 34px",
  background: "#fffaf7",
};

const inputStyle = (hasError) => ({
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: `1px solid ${hasError ? "#dc2626" : "#e7d9cf"}`,
  outline: "none",
  background: "#fff",
  fontSize: 14,
});

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const roomTypeLabel = (type) => {
  switch (type) {
    case "shared":
      return "Shared Room";
    case "private":
      return "Private Room";
    case "couple":
      return "Couple Room";
    case "duplex":
      return "Duplex Room";
    default:
      return type || "General";
  }
};

function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 700,
          marginBottom: 6,
          color: "#7f1d1d",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        {label}
      </label>
      {children}
      {error && <div style={{ marginTop: 6, color: "#dc2626", fontSize: 12 }}>{error}</div>}
    </div>
  );
}

export default function CourseBookingForm() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submittedSummary, setSubmittedSummary] = useState(EMPTY_SUBMITTED_SUMMARY);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${API_BASE}/courses`, {
          params: { withBatches: true },
        });
        setCourses(res.data?.data || []);
      } catch (err) {
        console.error("Failed to load courses", err);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    const preselectedCourse = searchParams.get("course");
    const preselectedBatch = searchParams.get("batch");

    if (preselectedCourse || preselectedBatch) {
      setForm((prev) => ({
        ...prev,
        courseId: preselectedCourse || prev.courseId,
        batchId: preselectedBatch || prev.batchId,
        roomType: "",
      }));
    }
  }, [searchParams]);

  const selectedCourse = useMemo(
    () => courses.find((course) => course._id === form.courseId) || null,
    [courses, form.courseId]
  );

  const batches = useMemo(() => selectedCourse?.batches || [], [selectedCourse]);

  const selectedBatch = useMemo(
    () => batches.find((batch) => batch._id === form.batchId) || null,
    [batches, form.batchId]
  );

  const roomOptions = useMemo(() => selectedBatch?.roomOptions || [], [selectedBatch]);

  useEffect(() => {
    if (!selectedCourse) return;
    const hasSelectedBatch = batches.some((batch) => batch._id === form.batchId);
    if (!hasSelectedBatch) {
      setForm((prev) => ({ ...prev, batchId: "", roomType: "" }));
    }
  }, [selectedCourse, batches, form.batchId]);

  useEffect(() => {
    if (!selectedBatch) return;
    const hasSelectedRoom = roomOptions.some((option) => option.type === form.roomType);
    if (!hasSelectedRoom) {
      setForm((prev) => ({ ...prev, roomType: "" }));
    }
  }, [selectedBatch, roomOptions, form.roomType]);

  const selectedRoom = useMemo(
    () => roomOptions.find((option) => option.type === form.roomType) || null,
    [roomOptions, form.roomType]
  );

  const activeSummary = submitted
    ? submittedSummary
    : {
        courseTitle: selectedCourse?.title || "Not selected",
        batchLabel: selectedBatch ? formatBatchLabel(selectedBatch) : "Not selected",
        roomLabel: selectedRoom ? selectedRoom.label : "Not selected",
        price: selectedRoom?.price || "-",
        seatsLeft: selectedRoom?.availableSeats ?? selectedBatch?.availableSeats ?? 0,
      };

  const validate = () => {
    const nextErrors = {};

    if (!form.firstName.trim()) nextErrors.firstName = "First name is required";
    if (!form.lastName.trim()) nextErrors.lastName = "Last name is required";
    if (!form.email.trim()) nextErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) nextErrors.email = "Invalid email";
    if (!form.phone.trim()) nextErrors.phone = "Phone is required";
    if (!form.country) nextErrors.country = "Please select a country";
    if (!form.address.trim()) nextErrors.address = "Address is required";
    if (!form.courseId) nextErrors.courseId = "Please select a course";
    if (!form.batchId) nextErrors.batchId = "Please select a batch";
    if (!form.roomType) nextErrors.roomType = "Please select a booking type";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "courseId") {
        next.batchId = "";
        next.roomType = "";
      }
      if (name === "batchId") {
        next.roomType = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validate()) return;

    setLoading(true);
    try {
      const nextSummary = {
        courseTitle: selectedCourse?.title || "Not selected",
        batchLabel: selectedBatch ? formatBatchLabel(selectedBatch) : "Not selected",
        roomLabel: selectedRoom ? selectedRoom.label : "Not selected",
        price: selectedRoom?.price || "-",
        seatsLeft: selectedRoom?.availableSeats ?? selectedBatch?.availableSeats ?? 0,
      };

      await axios.post(`${API_BASE}/course-bookings`, {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        address: form.address,
        country: form.country,
        source: "Course Booking Form",
        course: form.courseId,
        batch: form.batchId,
        roomType: form.roomType,
        seats: 1,
        status: "pending",
      });

      setSubmittedSummary(nextSummary);
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      setSubmitError(err.response?.data?.message || "Failed to submit booking.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(185,28,28,0.16), transparent 32%), #f6efe9",
        padding: "24px 16px 48px",
      }}
    >
      <div style={cardStyle}>
        <div style={leftStyle}>
          <div style={{ fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Dynamic Booking
          </div>
          <h1 style={{ fontSize: 42, lineHeight: 1.1, margin: "18px 0 14px" }}>
            Secure Your Seat
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.8, opacity: 0.92 }}>
            Select a real course, live batch, and available room type. Your request
            is saved as a booking and can be confirmed or cancelled by admin while
            seat availability updates automatically.
          </p>

          <div
            style={{
              marginTop: 28,
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 18,
              padding: 20,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Selected Summary</div>
            <div style={{ marginBottom: 8 }}>
              <strong>Course:</strong> {activeSummary.courseTitle}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Batch:</strong> {activeSummary.batchLabel}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Type:</strong> {activeSummary.roomLabel}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Price:</strong> {activeSummary.price}
            </div>
            <div>
              <strong>Seats Left:</strong> {activeSummary.seatsLeft}
            </div>
          </div>
        </div>

        <div style={rightStyle}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "50px 10px" }}>
              <div style={{ fontSize: 54, marginBottom: 18 }}>✓</div>
              <h2 style={{ color: "#7f1d1d", marginBottom: 12 }}>Booking Submitted</h2>
              <p style={{ color: "#6b4b4b", lineHeight: 1.8 }}>
                Your booking request has been saved as pending. Admin can now confirm
                or cancel it, and confirmed bookings will reduce available seats.
              </p>
              <div
                style={{
                  margin: "22px auto 0",
                  maxWidth: 420,
                  textAlign: "left",
                  background: "#fff",
                  border: "1px solid #ead7c8",
                  borderRadius: 16,
                  padding: 18,
                  boxShadow: "0 12px 30px rgba(127, 29, 29, 0.08)",
                }}
              >
                <div style={{ fontWeight: 700, color: "#7f1d1d", marginBottom: 10 }}>
                  Selected Summary
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Course:</strong> {submittedSummary.courseTitle}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Batch:</strong> {submittedSummary.batchLabel}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Type:</strong> {submittedSummary.roomLabel}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Price:</strong> {submittedSummary.price}
                </div>
                <div>
                  <strong>Seats Left:</strong> {submittedSummary.seatsLeft}
                </div>
              </div>
            </div>
          ) : (
            <>
              <h2 style={{ color: "#7f1d1d", marginBottom: 8 }}>Course Booking Form</h2>
              <p style={{ color: "#7b6b63", marginBottom: 22 }}>
                Fill in your details and choose an available batch and room type.
              </p>

              {submitError && (
                <div className="alert alert-danger" style={{ marginBottom: 18 }}>
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Field label="First Name" error={errors.firstName}>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      style={inputStyle(errors.firstName)}
                    />
                  </Field>
                  <Field label="Last Name" error={errors.lastName}>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      style={inputStyle(errors.lastName)}
                    />
                  </Field>
                </div>

                <Field label="Email" error={errors.email}>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    style={inputStyle(errors.email)}
                  />
                </Field>

                <Field label="Phone" error={errors.phone}>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    style={inputStyle(errors.phone)}
                  />
                </Field>

                <Field label="Country" error={errors.country}>
                  <select
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    style={inputStyle(errors.country)}
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Address" error={errors.address}>
                  <input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    style={inputStyle(errors.address)}
                  />
                </Field>

                <Field label="Course" error={errors.courseId}>
                  <select
                    name="courseId"
                    value={form.courseId}
                    onChange={handleChange}
                    style={inputStyle(errors.courseId)}
                  >
                    <option value="">Select course</option>
                    {courses.map((course) => (
                      <option key={course._id} value={course._id}>
                        {course.shortTitle || course.title}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Batch" error={errors.batchId}>
                  <select
                    name="batchId"
                    value={form.batchId}
                    onChange={handleChange}
                    style={inputStyle(errors.batchId)}
                    disabled={!selectedCourse}
                  >
                    <option value="">Select batch</option>
                    {batches.map((batch) => (
                      <option key={batch._id} value={batch._id} disabled={batch.availableSeats <= 0}>
                        {formatBatchLabel(batch)} ({batch.availableSeats} seats left)
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Booking Type" error={errors.roomType}>
                  <select
                    name="roomType"
                    value={form.roomType}
                    onChange={handleChange}
                    style={inputStyle(errors.roomType)}
                    disabled={!selectedBatch}
                  >
                    <option value="">Select room type</option>
                    {roomOptions.map((option) => (
                      <option
                        key={option.type}
                        value={option.type}
                        disabled={option.availableSeats <= 0}
                      >
                        {option.label} - {option.price || "-"} ({option.availableSeats} seats)
                      </option>
                    ))}
                  </select>
                </Field>

                {selectedRoom && (
                  <div
                    style={{
                      marginBottom: 18,
                      background: "#fff",
                      border: "1px solid #ead7c8",
                      borderRadius: 16,
                      padding: 16,
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#7f1d1d", marginBottom: 8 }}>
                      {roomTypeLabel(selectedRoom.type)}
                    </div>
                    <div style={{ color: "#6b4b4b", lineHeight: 1.7 }}>
                      Price: {selectedRoom.price || "-"}
                      <br />
                      Old Price: {selectedRoom.priceOld || "-"}
                      <br />
                      Available Seats: {selectedRoom.availableSeats}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    border: "none",
                    borderRadius: 14,
                    padding: "14px 18px",
                    background: "linear-gradient(135deg, #7f0000, #c0392b)",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.75 : 1,
                  }}
                >
                  {loading ? "Submitting..." : "Submit Booking"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatBatchLabel(batch) {
  return `${formatDate(batch.startDate)} - ${formatDate(batch.endDate)}`;
}
